import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Tooltip from '@mui/material/Tooltip';

export const STATUS_COLORS = {
  PASS: '#2e7d32',
  FAIL: '#d32f2f',
  NA: '#1976d2',
  WARNING: '#997733',
} as const;

export const getStatusIcon = (status?: string) => {
  const normalized = status?.toUpperCase();
  if (normalized === 'PASS') {
    return <CheckCircleOutlineIcon fontSize="small" sx={{ color: STATUS_COLORS.PASS }} />;
  }
  if (normalized === 'FAIL') {
    return <ReportProblemOutlinedIcon fontSize="small" sx={{ color: STATUS_COLORS.FAIL }} />;
  }
  if (normalized === 'NA') {
    return (
      <Tooltip title="No defined validation rule.">
        <RadioButtonUncheckedIcon fontSize="small" sx={{ color: STATUS_COLORS.NA }} />
      </Tooltip>
    );
  }
  return null;
};

export const getStatusColor = (status?: string) => {
  const normalized = status?.toUpperCase();
  if (normalized === 'PASS') return STATUS_COLORS.PASS;
  if (normalized === 'FAIL') return STATUS_COLORS.FAIL;
  if (normalized === 'NA') return STATUS_COLORS.NA;
  return undefined;
};

type ParsedFonts = {
  actualFonts: string[];
  requiredFonts: string[];
  optionalFonts: string[];
  expectedUnknown: boolean;
};

const toList = (value: string) => {
  const tokens = value
    .split(/[;]/)
    .map(font => font.trim())
    .filter(Boolean);

  const merged: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const current = tokens[i];
    const next = tokens[i + 1];

    // Merge a font name followed by a lone "bold" token (any casing).
    if (current && next && /^bold$/i.test(next)) {
      merged.push(`${current}, ${next}`);
      i += 1;
      continue;
    }

    merged.push(current);
  }

  return merged.sort((a, b) => a.localeCompare(b));
};

export const parseFontCheck = (fontString: string): ParsedFonts => {
  const [actualPart = '', expectedPartRaw = ''] = fontString.split(/\*{3}/);
  const cleanedLabel = expectedPartRaw
    .replace(/^\s*\*+\s*/g, '')
    .replace(/^\s*(vaild|valid)\s+fonts?:?/i, '')
    .replace(/\*+\s*(vaild|valid)\s+fonts?:?/i, '')
    .trim();

  const expectedList = toList(cleanedLabel);
  const expectedUnknown = expectedList.length === 1 && expectedList[0].toLowerCase() === 'no';
  const optionalFonts = expectedList
    .filter(font => /\(o\)$/i.test(font))
    .map(font => font.replace(/\s*\(o\)$/i, '').trim());
  const requiredFonts = expectedUnknown
    ? []
    : expectedList.filter(font => !/\(o\)$/i.test(font) && font.toLowerCase() !== 'no');

  return {
    actualFonts: toList(actualPart),
    requiredFonts,
    optionalFonts,
    expectedUnknown,
  };
};
